import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { endpoints } from '@/api'
import { keys } from '@/api/keys'
import type { ListNotificationsQuery } from '@/api/endpoints/tenancy'
import { useCan } from '@/auth'

/** Poll the unread badge every 60 s while the tab is visible (FRONTEND_PLAN BE-22: no realtime). */
export const UNREAD_POLL_MS = 60_000

export function useUnreadCount() {
  const canRead = useCan('notifications:read')
  return useQuery({
    queryKey: keys.notifications.unread,
    queryFn: () => endpoints.notifications.unreadCount(),
    enabled: canRead,
    refetchInterval: (query) =>
      document.visibilityState === 'visible' && query.state.status !== 'error'
        ? UNREAD_POLL_MS
        : false,
  })
}

export function useNotifications(filters: ListNotificationsQuery) {
  const canRead = useCan('notifications:read')
  return useQuery({
    queryKey: keys.notifications.list(filters),
    queryFn: () => endpoints.notifications.list(filters),
    enabled: canRead,
  })
}

export function useMarkRead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => endpoints.notifications.markRead(id),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.notifications.all }),
  })
}

export function useMarkAllRead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => endpoints.notifications.markAllRead(),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.notifications.all }),
  })
}
